// Contribution from Mercatura DeFi Team to Menese Protocol
// (2-of-2 Threshold ECDSA — Tier 7 device-side flow orchestration).
// Mercatura Forum proprietary.
//
// High-level register / sign flows that wire `passkey` + `wasm` +
// `canister/agent` together. The four pages in §3.H call these.
//
// **Design note**: `x_user` is a 32-byte secp256k1 ECDSA scalar that
// the device must hold for every sign call. We:
//   1. Generate it ONCE at register time via the wasm-bindgen
//      `nim_keygen_user_share` (uses `window.crypto.getRandomValues`).
//   2. Wrap it under the WebAuthn-PRF passkey-derived KEK.
//   3. Send the wrapped blob to the canister at register.
//   4. On every sign: fetch the wrapped blob, unwrap with the same
//      passkey, build the user contribution, send to canister,
//      verify the returned signature locally.
//
// Anti-stub posture: every error path surfaces a real error to the
// caller. No silent fallbacks.

import init, * as nim from "./wasm/menese_nim_2party_wasm.js";
import {
  hasLocalCredentialId,
  passkeyDerivePrfSalt,
  passkeyRegister,
  unwrapXUser,
  wrapXUser,
  type EncryptedBlob,
} from "./passkey";
import type { MeneseCanister } from "./canister/agent";
import type { Principal } from "@dfinity/principal";
import type { RegisterUserPubkeyArgs } from "./canister/types";

let wasmInited = false;
async function ensureWasm(): Promise<void> {
  if (!wasmInited) {
    await init();
    wasmInited = true;
  }
}

// ---------------------------------------------------------------------------
// Session-only critical-path caches.
//
// The protocol is one-round NIM, so the SIGN critical path needs only one
// IC update call (`sign_via_nim`). The other two roundtrips
// (`fetch_encrypted_blob` query + `fetch_nim_presig` update) move off the
// critical path:
//   1. `cachedXUser` — set the first time the device unwraps the AEAD blob.
//      Cleared on logout. Cuts ~100-200ms per subsequent sign.
//   2. `cachedPresig` — pre-fetched in the background by `prefetchPresig`
//      after authentication and after each successful sign. Drained
//      atomically by the next `signMessage` call. Cuts ~500ms-2s per sign
//      on the critical path. Falls back to synchronous fetch on cache miss.
//
// Both caches are RAM-only (never persisted to localStorage / IndexedDB /
// cookies). The presig payload is public; the user-share secret is NOT —
// `cachedXUser` lives only for the page session and is best-effort zeroed
// at logout.
//
// Test seam: when the page is in test mode (URL has
// `?menese_test_seed=<hex>`), `prefetchPresig` flips
// `window.__MENESE_PRESIG_READY__` to `true` once the cache is full. The
// e2e specs `await page.waitForFunction(() => window.__MENESE_PRESIG_READY__)`
// before timing a "warm" sign so the measurement is not racing the
// background prefetch.
// ---------------------------------------------------------------------------

const PRESIG_CACHE_TTL_MS = 5 * 60 * 1000;

interface CachedPresig {
  pid: Uint8Array;
  canisterPublicBytes: Uint8Array;
  canisterClPubkeyBytes: Uint8Array;
  productionClCrsBytes: Uint8Array;
  fetchedAtMs: number;
}

let cachedXUser: Uint8Array | null = null;
let cachedPresig: CachedPresig | null = null;
let prefetchInFlight: Promise<void> | null = null;

function setPresigReadyFlag(ready: boolean): void {
  // Test seam — only set when the test-API surface exists (gated on
  // `?menese_test_seed=...`). Production pages never see this flag.
  const w = window as unknown as { __MENESE_PRESIG_READY__?: boolean };
  if (typeof window !== "undefined") {
    w.__MENESE_PRESIG_READY__ = ready;
  }
}

/** Drop the per-session caches. Called by `onLogout` in `main.ts`. */
export function clearSessionCache(): void {
  if (cachedXUser) {
    // Best-effort zero of the secret in JS heap. JS does not guarantee
    // the underlying buffer is freed, but a zeroed view reduces
    // accidental exposure on later heap dumps.
    cachedXUser.fill(0);
  }
  cachedXUser = null;
  cachedPresig = null;
  prefetchInFlight = null;
  setPresigReadyFlag(false);
}

/** Background-prefetch one NIM presig and store it in the session cache.
 *  Idempotent — a fresh cache short-circuits; concurrent calls dedupe via
 *  `prefetchInFlight`. Failures (e.g. canister not yet registered) leave
 *  the cache empty so the next sign falls back to synchronous fetch and
 *  surfaces the real error. */
export async function prefetchPresig(canister: MeneseCanister): Promise<void> {
  if (cachedPresig && Date.now() - cachedPresig.fetchedAtMs < PRESIG_CACHE_TTL_MS) {
    setPresigReadyFlag(true);
    return;
  }
  if (prefetchInFlight) return prefetchInFlight;
  setPresigReadyFlag(false);
  prefetchInFlight = (async () => {
    try {
      const reply = await canister.fetchNimPresig();
      if (!("Ok" in reply)) return;
      const ok = (reply as {
        Ok: {
          pid: Uint8Array;
          canister_public: Uint8Array;
          canister_cl_pubkey: Uint8Array;
          production_cl_crs: Uint8Array;
        };
      }).Ok;
      cachedPresig = {
        pid: ok.pid,
        canisterPublicBytes: ok.canister_public,
        canisterClPubkeyBytes: ok.canister_cl_pubkey,
        productionClCrsBytes: ok.production_cl_crs,
        fetchedAtMs: Date.now(),
      };
      setPresigReadyFlag(true);
    } finally {
      prefetchInFlight = null;
    }
  })();
  return prefetchInFlight;
}

function consumeCachedPresig(): CachedPresig | null {
  const p = cachedPresig;
  cachedPresig = null;
  setPresigReadyFlag(false);
  return p;
}

/** Verify that the canister, the wasm bindings, and this TS module
 *  agree on `MENESE_COMBINED_VERSION` (Tier-7 §1.I). Caller must
 *  invoke this BEFORE any other flow function — the page entry-point
 *  in `App.tsx` does so. */
export async function assertCombinedVersionMatches(
  canister: MeneseCanister,
  expectedVersion: number,
): Promise<void> {
  await ensureWasm();
  const wasmVersion = nim.menese_combined_version();
  const status = await canister.canisterStatus();
  // The canister exposes `protocol_version` not `combined_version`
  // directly today; we tie protocol_version === MENESE_COMBINED_VERSION
  // because they version together (the canister .did declares
  // protocol_version: nat32).
  const canisterVersion = status.protocol_version;
  if (
    wasmVersion !== expectedVersion ||
    canisterVersion !== expectedVersion
  ) {
    throw new Error(
      `MENESE_COMBINED_VERSION mismatch: ts=${expectedVersion} ` +
        `wasm=${wasmVersion} canister=${canisterVersion}. Refusing to load (§1.I).`,
    );
  }
}

export interface OnboardingResult {
  /** SEC1-compressed user pubkey (33 bytes). Public information. */
  userPubkeyCompressed: Uint8Array;
  /** Combined pubkey returned by the canister after register
   *  succeeds. SEC1-compressed (33 bytes). */
  combinedPubkey: Uint8Array;
}

/** First-time onboarding for a freshly-logged-in II principal.
 * Generates `x_user`, wraps under WebAuthn-PRF, registers on the
 * canister.
 *
 * Returns the public combined pubkey on success; throws on any
 * step's failure. */
export async function onboardingRegister(opts: {
  canister: MeneseCanister;
  iiPrincipal: Principal;
}): Promise<OnboardingResult> {
  await ensureWasm();

  // Step 1: pull canister's production CL CRS so we can derive a
  // matching device-side CL keypair.
  const crsBytes = await opts.canister.canisterProductionClCrs();
  if (crsBytes.byteLength === 0) {
    throw new Error("canister_production_cl_crs returned empty (canister not initialized)");
  }

  // Step 2: register a fresh WebAuthn passkey.
  const handle = await passkeyRegister(opts.iiPrincipal);
  // Re-derive the PRF salt (the `create()` call already returned it,
  // but we use the unified `passkeyDerivePrfSalt` here so wrap/unwrap
  // share one path). The cred id is in localStorage from `passkeyRegister`.
  void handle;
  const { prfSalt } = await passkeyDerivePrfSalt();

  // Step 3: generate fresh x_user secp256k1 share.
  const shareBytes = nim.nim_keygen_user_share();
  // Format: 32 scalar bytes || 33 SEC1 pubkey
  const xUser32 = shareBytes.slice(0, 32);
  const userPubkeyCompressed = shareBytes.slice(32, 65);

  // Step 4: derive matching CL secret + pubkey under canister CRS.
  // The CL secret is a deterministic function of x_user — bind by
  // hashing x_user together with the canister id. This means
  // recovering x_user is sufficient to recover the CL keypair too;
  // the only secret is x_user (the AEAD-wrapped one).
  const clSecret = await deriveDeviceClSecretFromXUser(xUser32);
  const userClPubkeyBytes = nim.nim_keygen_user_cl_keypair_from_secret(
    crsBytes,
    clSecret,
  );

  // Step 5: AEAD-wrap x_user under the PRF-derived KEK.
  const blob = await wrapXUser(prfSalt, xUser32);

  // Step 6: register on canister.
  const args: RegisterUserPubkeyArgs = {
    user_pubkey: userPubkeyCompressed,
    user_cl_pubkey: userClPubkeyBytes,
    encrypted_blob: blob,
  };
  const reply = await opts.canister.registerUserPubkey(args);
  if (!("Ok" in reply)) {
    throw new Error(`register_user_pubkey: ${JSON.stringify(reply)}`);
  }

  // Step 7: read canister-reported combined pubkey.
  const cpkReply = await opts.canister.combinedPublicKey();
  if (!("Ok" in cpkReply)) {
    throw new Error("combined_public_key: NotRegistered after Ok register");
  }
  const combinedPubkey = cpkReply.Ok;
  return { userPubkeyCompressed, combinedPubkey };
}

export interface SignResult {
  r: Uint8Array;
  s: Uint8Array;
  combinedPubkey: Uint8Array;
  messageHash: Uint8Array;
  presigId: Uint8Array;
}

/** Sign a message under the registered combined ECDSA pubkey. The
 *  caller has already authenticated via II (`opts.iiPrincipal` is a
 *  non-anonymous principal). Cross-device: works from any device that
 *  has the same passkey synced (via Apple Keychain, Google Password
 *  Manager, 1Password, hardware key, etc.).
 *
 *  Critical-path optimization: this routine drains the session-level
 *  `cachedXUser` and `cachedPresig` (populated by `onAuthenticated` /
 *  `prefetchPresig`) so the only IC roundtrip on the warm path is the
 *  single `sign_via_nim` update call. On cache miss the routine falls
 *  back to synchronous `fetch_encrypted_blob` + `fetch_nim_presig`
 *  with the same algebra. After `sign_via_nim` returns Ok, the next
 *  presig is fire-and-forget pre-fetched so a subsequent sign is
 *  again warm. */
export async function signMessage(opts: {
  canister: MeneseCanister;
  message: Uint8Array;
}): Promise<SignResult> {
  await ensureWasm();
  void hasLocalCredentialId; // imported but only meaningful for UI hints

  // Step 1+2: x_user — RAM cache hit short-circuits fetch + unwrap.
  let xUser32 = cachedXUser;
  if (!xUser32) {
    const blobReply = await opts.canister.fetchEncryptedBlob();
    if (!("Ok" in blobReply)) {
      throw new Error("fetch_encrypted_blob: NotRegistered");
    }
    const blob = (blobReply as { Ok: EncryptedBlob }).Ok;
    const { prfSalt } = await passkeyDerivePrfSalt();
    const unwrapped = await unwrapXUser(prfSalt, blob);
    if (unwrapped === null) {
      throw new Error("unwrap failed (wrong passkey or tampered blob)");
    }
    xUser32 = unwrapped;
    cachedXUser = unwrapped;
  }

  // Step 3: derive matching CL secret.
  const clSecret = await deriveDeviceClSecretFromXUser(xUser32);

  // Step 4: NIM presig — drain prefetch cache on hit; synchronous
  // `fetch_nim_presig` on miss. The PoK on the cached presig is
  // verified inside `nim_build_user_request` below, so a stale or
  // tampered cache cannot bypass the §3.G binding.
  //
  // Race-prevention: serialize against any in-flight prefetch. The
  // canister's `fetch_nim_presig` currently pop+reinserts pool
  // entries, so two concurrent fetches return the SAME pid. If we
  // didn't wait, a sync fetch fallback could collide with an
  // outstanding prefetch and both contributions would target the same
  // pid — second sign hits ConsumedPresig. (T7-followup: canister-side
  // fix moves popped entries to a separate IN_FLIGHT map so the race
  // can't happen cross-device.)
  if (prefetchInFlight) {
    try { await prefetchInFlight; } catch { /* fall through to sync fetch */ }
  }
  let canisterPublicBytes: Uint8Array;
  let canisterClPubkeyBytes: Uint8Array;
  let productionClCrsBytes: Uint8Array;
  const cached = consumeCachedPresig();
  if (cached && Date.now() - cached.fetchedAtMs < PRESIG_CACHE_TTL_MS) {
    canisterPublicBytes = cached.canisterPublicBytes;
    canisterClPubkeyBytes = cached.canisterClPubkeyBytes;
    productionClCrsBytes = cached.productionClCrsBytes;
  } else {
    const fetchReply = await opts.canister.fetchNimPresig();
    if (!("Ok" in fetchReply)) {
      throw new Error(`fetch_nim_presig: ${JSON.stringify(fetchReply)}`);
    }
    const fetchOk = (fetchReply as {
      Ok: {
        pid: Uint8Array;
        canister_public: Uint8Array;
        canister_cl_pubkey: Uint8Array;
        production_cl_crs: Uint8Array;
      };
    }).Ok;
    canisterPublicBytes = fetchOk.canister_public;
    canisterClPubkeyBytes = fetchOk.canister_cl_pubkey;
    productionClCrsBytes = fetchOk.production_cl_crs;
  }

  // Step 5: SHA-256 hash the message.
  const messageHashBuf = await crypto.subtle.digest("SHA-256", opts.message as BufferSource);
  const messageHash = new Uint8Array(messageHashBuf);

  // Step 6: build the one-round contribution via wasm. Internally
  // verifies the canister's §3.G PoK against the user's CL pk —
  // load-bearing security check that catches a tampered presig before
  // it touches `sign_via_nim`.
  const tweak32 = new Uint8Array(32);
  const seedBytes = crypto.getRandomValues(new Uint8Array(8));
  const requestBytes = nim.nim_build_user_request(
    productionClCrsBytes,
    canisterPublicBytes,
    canisterClPubkeyBytes,
    clSecret,
    xUser32,
    messageHash,
    tweak32,
    seedBytes,
  );

  // Step 7: sign via canister — the one critical-path IC call.
  const signReply = await opts.canister.signViaNim(requestBytes);
  if (!("Ok" in signReply)) {
    throw new Error(`sign_via_nim: ${JSON.stringify(signReply)}`);
  }
  const signatureBincode = (signReply as { Ok: { signature: Uint8Array } }).Ok.signature;

  // Background-refill the presig cache for the NEXT sign. Fire and
  // forget; if it errors (e.g. user logged out mid-flight) the next
  // sign falls back to synchronous fetch and surfaces the real error.
  void prefetchPresig(opts.canister);

  // Step 8: parse the bincode FinalNimSignature. We don't need wasm
  // here — the bincode layout for `FinalNimSignature` is:
  //   [u8;32] presig_id, [u8;32] r_bytes, [u8;32] s_bytes,
  //   Vec<u8> combined_pubkey_bytes (length-prefixed by 8-byte LE u64).
  if (signatureBincode.byteLength < 32 + 32 + 32 + 8) {
    throw new Error("FinalNimSignature bincode too short");
  }
  const presigId = signatureBincode.slice(0, 32);
  const r = signatureBincode.slice(32, 64);
  const s = signatureBincode.slice(64, 96);
  const cpkLenBytes = signatureBincode.slice(96, 104);
  const cpkLen = readU64Le(cpkLenBytes);
  const combinedPubkey = signatureBincode.slice(104, 104 + Number(cpkLen));
  if (combinedPubkey.byteLength !== Number(cpkLen)) {
    throw new Error("FinalNimSignature combined_pubkey length mismatch");
  }

  // Step 9: external local verify.
  const ok = nim.nim_verify_signature_under_combined_pk(
    messageHash,
    r,
    s,
    combinedPubkey,
  );
  if (!ok) {
    throw new Error("local k256 verify_prehash failed (canister returned bad signature)");
  }

  return { r, s, combinedPubkey, messageHash, presigId };
}

function readU64Le(b: Uint8Array): bigint {
  let v = 0n;
  for (let i = 0; i < 8; i++) v |= BigInt(b[i]) << BigInt(8 * i);
  return v;
}

/** Bind the device's CL secret to its `x_user` via SHA-256.
 *  Means recovering `x_user` (via passkey unwrap) is sufficient to
 *  recover the CL keypair too; we don't store the CL secret separately.
 *  The HKDF info string `"menese-2of2-cl-secret-from-x_user-v1"`
 *  domain-separates this from any other key derivation. */
async function deriveDeviceClSecretFromXUser(xUser32: Uint8Array): Promise<Uint8Array> {
  const ikm = await crypto.subtle.importKey(
    "raw",
    xUser32 as BufferSource,
    "HKDF",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(0),
      info: new TextEncoder().encode("menese-2of2-cl-secret-from-x_user-v1"),
    },
    ikm,
    256,
  );
  return new Uint8Array(bits);
}
